import { Column, Entity, JoinColumn, ManyToOne, OneToMany } from 'typeorm';
import { BaseEntity } from './base.entity';
import type { SalesTripEntity } from './sales-trip.entity';
import type { UserEntity } from './user.entity';

/** Lifecycle of a multi-day plan and of each of its trips (decision 16: no in_progress). */
export type SalesTripStatus = 'draft' | 'planned' | 'completed' | 'cancelled';

/** How a trip status reads on screen; the stored codes are lower case. */
export const SALES_TRIP_STATUS_LABELS: Record<SalesTripStatus, string> = {
	draft: 'Draft',
	planned: 'Planned',
	completed: 'Completed',
	cancelled: 'Cancelled',
};

/**
 * A multi-day sales plan — the container that groups the trips of "3 days in
 * Boston" or a 10-day rotation so they can be opened, re-grouped and
 * re-optimized as one thing (PD-15920, docs/claude/sales-routes/02-data-model.md).
 * Optional: a single-day trip needs no plan.
 */
@Entity('sales_trip_plan')
export class SalesTripPlanEntity extends BaseEntity {
	/** The rep the plan is FOR (workd user id) — ownership for `:self` scoping. */
	@Column({ name: 'fk_user__id_salesperson', type: 'int', unsigned: true })
	public salespersonUserId!: number;

	@Column({ name: 'name', type: 'varchar', length: 255 })
	public name!: string;

	@Column({ name: 'startDate', type: 'date' })
	public startDate!: string;

	@Column({ name: 'endDate', type: 'date' })
	public endDate!: string;

	/** Free-text region the rep chose, e.g. "Boston, 3 days". */
	@Column({ name: 'regionLabel', type: 'varchar', length: 255, nullable: true })
	public regionLabel?: string | null;

	@Column({ name: 'status', type: 'varchar', length: 20, default: "'draft'" })
	public status!: SalesTripStatus;

	@ManyToOne('UserEntity', {
		nullable: true,
		createForeignKeyConstraints: false,
	})
	@JoinColumn([
		{ name: 'fk_user__id_salesperson', referencedColumnName: 'workdUserId' },
	])
	public salesperson?: UserEntity | null;

	@OneToMany('SalesTripEntity', (trip: SalesTripEntity) => trip.fkSalesTripPlan)
	public trips?: Array<SalesTripEntity>;
}
